import React from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { GLYPHS } from '@/components/ui';
import { getActiveEmployeeCountForMonth, calculateBusinessScale } from '../../utils/businessScale';
import type { Employee } from '../../types/employee';
import { useCompanySizeThreshold } from '../../hooks/useRates';

const APPLIED_LAW_5_MINUS = [
  '연장·야간 수당 의무 없음',
  '연차 휴가 의무 없음',
  '주 52시간 미적용',
];

const APPLIED_LAW_5_PLUS = [
  '연장·야간 수당 50% 가산',
  '연차 휴가 부여 의무',
  '주 52시간 적용',
  '4시간당 30분 휴게 의무',
];

const CHECKLIST_5_MINUS = [
  '가산수당 미적용 → 노동청 리스크 없음',
  '3개월 이상 근무자 해고 시 30일분 예고 수당 확인',
];

const CHECKLIST_5_PLUS = [
  '주 52시간 초과 여부 확인',
  '연차 미부여 시 리스크',
  '해고 시 서면 통지 권장',
  '예고 없이 해고 시 30일분 임금 추가 지급',
];

interface BusinessScaleCardProps {
  employees: Employee[];
  selectedMonth: Date;
  accountNumber: number;
  /** 오버라이드 반영된 5인 이상 여부. 전달 시 표시·법률 문구에 사용 */
  isBusinessScale5Plus?: boolean;
  /** 5인 미만으로 오버라이드 선택된 상태 (제목 "(선택)" 표시용) */
  isOverrideUnder5?: boolean;
  /** 5인 미만/이상 선택 변경 콜백 */
  onSetOverride?: (under5: boolean) => void | Promise<void>;
}

const BusinessScaleCard: React.FC<BusinessScaleCardProps> = ({
  employees,
  selectedMonth,
  accountNumber,
  isBusinessScale5Plus,
  isOverrideUnder5,
  onSetOverride,
}) => {
  const threshold = useCompanySizeThreshold();
  const activeCount = getActiveEmployeeCountForMonth(employees, selectedMonth, accountNumber);
  const computed5Plus = calculateBusinessScale(employees, selectedMonth, accountNumber);
  const effective5Plus = isBusinessScale5Plus ?? computed5Plus;
  const isActualCount5Plus = activeCount >= threshold;

  const appliedLaw = effective5Plus ? APPLIED_LAW_5_PLUS : APPLIED_LAW_5_MINUS;
  const checklist = effective5Plus ? CHECKLIST_5_PLUS : CHECKLIST_5_MINUS;

  const titleText =
    !effective5Plus && isOverrideUnder5
      ? '5인 미만 사업장 (선택)'
      : effective5Plus
        ? '5인 이상 사업장'
        : '5인 미만 사업장';

  return (
    <Accordion
      defaultExpanded={false}
      sx={{
        mb: 2,
        borderRadius: 1,
        border: '1px solid var(--border-default)',
        background: '#fff',
        '&:before': { display: 'none' },
        boxShadow: 'none',
      }}
    >
      <AccordionSummary expandIcon={<Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{GLYPHS.down}</Box>}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {titleText}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              재직 중인 직원 {activeCount}명 기준
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {onSetOverride && (
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {isActualCount5Plus && !isOverrideUnder5 && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onSetOverride(true)}
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              >
                5인 미만으로 선택
              </Button>
            )}
            {isOverrideUnder5 && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => onSetOverride(false)}
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              >
                5인 이상으로 되돌리기
              </Button>
            )}
            {isOverrideUnder5 && isActualCount5Plus && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  5인 이상 스케줄이지만, 5인 미만으로 선택되어 있습니다. 현재 5인 이상이라 직원의 스케줄 조정이 필요합니다.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            적용되는 법
          </Typography>
          <List dense disablePadding sx={{ listStyle: 'disc', pl: 2 }}>
            {appliedLaw.map((item, i) => (
              <ListItem key={i} disablePadding sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            꼭 확인할 것
          </Typography>
          <List dense disablePadding sx={{ listStyle: 'disc', pl: 2 }}>
            {checklist.map((item, i) => (
              <ListItem key={i} disablePadding sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default BusinessScaleCard;
